import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { createStudioConfig, type CreateStudioConfigOptions } from '@neotech-solutions-org/sanity-cms';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useEffect, useMemo, useState } from 'react';
import { Studio } from 'sanity';
import { createStudioCapabilities, createStudioLanguages } from '../../config/sanity/studioAdapter';
import { useAvailableLanguages } from '../../config/sanity/useAvailableLanguages';
import { FEATURE_FLAG } from '../../constants/featureFlags';
import Log from '../../utils/logger';
import { getSanityConfig } from '../../utils/envDynamicValues';
import { useMarketplace } from '../../hooks/useMarketplace';
import { runtimeEnv } from '../../config/runtimeEnv';

const CMSStudioPage = () => {
  const user = useAuth();
  const availableLanguages = useAvailableLanguages();
  const languages = useMemo(() => createStudioLanguages(availableLanguages), [availableLanguages]);
  const { config: marketplaceConfig, isStandaloneCasino } = useMarketplace();
  const promotionsEnabled = useFeatureIsOn(FEATURE_FLAG.PROMOTIONS_ENABLED);
  const capabilities = useMemo(
    () =>
      createStudioCapabilities({
        permissions: user?.permissions ?? [],
        isStandaloneCasino,
        promotionsEnabled,
      }),
    [isStandaloneCasino, promotionsEnabled, user?.permissions]
  );
  const sanityConfig = useMemo(
    () => marketplaceConfig?.sanityConfig ?? getSanityConfig(),
    [marketplaceConfig?.sanityConfig]
  );

  const [config, setConfig] = useState<ReturnType<typeof createStudioConfig> | null>(null);

  useEffect(() => {
    let isCurrentConfig = true;
    setConfig(null);

    const setupConfig = async () => {
      if (!sanityConfig?.projectId || !user?.permissions) return;

      const options: CreateStudioConfigOptions = {
        projectId: runtimeEnv.sanityProjectId ?? sanityConfig.projectId,
        dataset: runtimeEnv.sanityDataset ?? sanityConfig.dataset,
        languages,
        capabilities,
      };
      const baseConfig = createStudioConfig(options);
      const themeUrl = runtimeEnv.sanityThemeUrl ?? sanityConfig.themeUrl ?? sanityConfig.sanityThemeUrl;

      if (!themeUrl) {
        setConfig(baseConfig);
        return;
      }

      try {
        const { theme } = await import(
          /* @vite-ignore */
          themeUrl
        );
        if (isCurrentConfig) {
          setConfig(createStudioConfig({ ...options, theme }));
        }
      } catch (error) {
        Log('Failed to load theme:', error);
        if (isCurrentConfig) {
          setConfig(baseConfig);
        }
      }
    };

    setupConfig();

    return () => {
      isCurrentConfig = false;
    };
  }, [capabilities, languages, sanityConfig, user?.permissions]);

  return <>{config && <Studio config={config} scheme='light' />}</>;
};

export default CMSStudioPage;
